/* eslint-disable no-console */
/* eslint-disable unicorn/prefer-module */

/**
 * ============================================================================
 * SERVICIO CENTRALIZADO DE TIPOS DE CAMBIO
 * ============================================================================
 *
 * Única fuente de verdad para UF, Dólar Chile y Dólar Perú.
 *
 * Estrategia de carga:
 *   - Desarrollo (localhost): JSON local directamente
 *   - Producción: fetch online → si falla → JSON local como backup
 *
 * Sin fallbacks hardcodeados: siempre usa el último valor real disponible.
 *
 * USO:
 *   - Módulos puros (calculateVentas, etc.): importar funciones directamente
 *   - Componentes React: usar useExchangeRates() que wrappea este service
 */

// ─── Cache singleton ─────────────────────────────────────────────────────────

const cache = {
  uf: null, // { "YYYY-MM-DD": number }
  dolarChile: null, // { "YYYY-MM-DD": number }
  dolarPeru: null, // { "YYYY-MM-DD": number }
  loaded: false,
  loadingPromise: null, // Para evitar cargas concurrentes
};

// ─── Helpers internos ────────────────────────────────────────────────────────

const isLocal = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

/**
 * Convierte array JSON [{ fecha, valor }] a mapa { "YYYY-MM-DD": number }
 */
const arrayToMap = (data, parseAsFloat = false) => {
  const map = {};
  (data || []).forEach((item) => {
    const val = parseAsFloat ? parseFloat(item.valor) : item.valor;
    if (!isNaN(val)) map[item.fecha] = val;
  });
  return map;
};

/**
 * Carga JSON local (fallback / desarrollo)
 */
const cargarDesdeLocal = async () => {
  const ufData = await import("../data/uf.json");
  const dolarCLData = await import("../data/dolarchile.json");
  const dolarPEData = await import("../data/dolarperu.json");

  return {
    uf: arrayToMap(ufData.default || ufData),
    dolarChile: arrayToMap(dolarCLData.default || dolarCLData),
    dolarPeru: arrayToMap(dolarPEData.default || dolarPEData, true),
  };
};

/**
 * Carga desde APIs online (producción)
 */
const cargarDesdeOnline = async () => {
  const [ufRes, dolarCLRes, dolarPERes] = await Promise.all([
    fetch("https://wecast.cl/services/uf/uf.json"),
    fetch("https://wecast.cl/services/dolar/dolar.json"),
    fetch("https://visiona.pe/services/dolar/dolar.json"),
  ]);

  if (!ufRes.ok || !dolarCLRes.ok || !dolarPERes.ok) {
    throw new Error("Fetch tipos de cambio falló");
  }

  const ufData = await ufRes.json();
  const dolarCLData = await dolarCLRes.json();
  const dolarPEData = await dolarPERes.json();

  return {
    uf: arrayToMap(Array.isArray(ufData) ? ufData : []),
    dolarChile: arrayToMap(Array.isArray(dolarCLData) ? dolarCLData : []),
    dolarPeru: arrayToMap(Array.isArray(dolarPEData) ? dolarPEData : [], true),
  };
};

/**
 * Busca el valor más reciente en un mapa para una fecha dada.
 * Si no encuentra la fecha exacta, busca la fecha anterior más cercana.
 */
const lookupConFallback = (mapa, fechaStr) => {
  if (!mapa || !fechaStr) return null;
  const fecha = fechaStr.split("T")[0];
  const valor = mapa[fecha];
  if (valor !== undefined) return valor;

  // Buscar fecha anterior más cercana
  const fechasDisponibles = Object.keys(mapa).sort().reverse();
  for (const f of fechasDisponibles) {
    if (f <= fecha) return mapa[f];
  }
  return null;
};

/**
 * Obtiene el último valor conocido de un mapa (la fecha más reciente).
 */
const getLastValue = (mapa) => {
  if (!mapa || Object.keys(mapa).length === 0) return null;
  const lastDate = Object.keys(mapa).sort().pop();
  return mapa[lastDate] || null;
};

// ─── API pública ─────────────────────────────────────────────────────────────

/**
 * Carga todos los tipos de cambio. Idempotente: si ya están cargados, retorna inmediatamente.
 * En producción intenta online primero, si falla usa JSON local como backup.
 */
export const loadExchangeRates = async () => {
  if (cache.loaded) return;
  if (cache.loadingPromise) return cache.loadingPromise;

  cache.loadingPromise = (async () => {
    try {
      let data;
      if (isLocal()) {
        data = await cargarDesdeLocal();
      } else {
        try {
          data = await cargarDesdeOnline();
        } catch (err) {
          console.warn("Fetch online falló, usando JSON local como backup:", err.message);
          data = await cargarDesdeLocal();
        }
      }
      cache.uf = data.uf;
      cache.dolarChile = data.dolarChile;
      cache.dolarPeru = data.dolarPeru;
      cache.loaded = true;
    } catch (error) {
      console.error("Error cargando tipos de cambio:", error);
      cache.uf = cache.uf || {};
      cache.dolarChile = cache.dolarChile || {};
      cache.dolarPeru = cache.dolarPeru || {};
      cache.loaded = true;
    } finally {
      cache.loadingPromise = null;
    }
  })();

  return cache.loadingPromise;
};

/**
 * Retorna true si los datos ya están cargados.
 */
export const isLoaded = () => cache.loaded;

// ─── Mapas crudos ────────────────────────────────────────────────────────────

export const getDolarMap = (pais) => (pais === "Chile" ? cache.dolarChile : cache.dolarPeru) || {};
export const getUFMap = () => cache.uf || {};

// ─── Lookups por fecha ───────────────────────────────────────────────────────

/**
 * Valor UF para una fecha. Fallback: último valor conocido.
 */
export const getUFValue = (fechaStr) => {
  const val = lookupConFallback(cache.uf, fechaStr);
  return val !== null ? val : getLastValue(cache.uf) || 0;
};

/**
 * Valor dólar para una fecha y país. Fallback: último valor conocido.
 */
export const getDolarValue = (fechaStr, pais) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const val = lookupConFallback(mapa, fechaStr);
  return val !== null ? val : getDolarFallback(pais);
};

/**
 * Último valor real conocido del dólar para un país (dinámico, sin hardcodeo).
 */
export const getDolarFallback = (pais) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  return getLastValue(mapa) || 0;
};

/**
 * Último valor real conocido de la UF (dinámico, sin hardcodeo).
 */
export const getUFFallback = () => getLastValue(cache.uf) || 0;

// ─── Dólar mensual ───────────────────────────────────────────────────────────

/**
 * Array de 12 valores: tipo de cambio dólar por mes para un país y año.
 * Para cada mes, toma el último valor disponible dentro del mes.
 * Si no hay datos para ese mes, busca la fecha anterior más cercana.
 */
export const calcularDolarMensual = (pais, year) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const fallback = getDolarFallback(pais);
  const valores = Array(12).fill(fallback);

  if (!mapa || Object.keys(mapa).length === 0) return valores;

  for (let mes = 0; mes < 12; mes++) {
    const ultimoDiaMes = new Date(year, mes + 1, 0).getDate();

    // Buscar último valor disponible dentro del mes
    let valorEncontrado = null;
    for (let dia = ultimoDiaMes; dia >= 1; dia--) {
      const fechaStr = `${year}-${String(mes + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
      if (mapa[fechaStr] !== undefined) {
        valorEncontrado = mapa[fechaStr];
        break;
      }
    }

    // Si no hay datos en el mes, buscar fecha anterior más cercana
    if (valorEncontrado === null) {
      const ultimoDiaStr = `${year}-${String(mes + 1).padStart(2, "0")}-${String(ultimoDiaMes).padStart(2, "0")}`;
      const fechasDisponibles = Object.keys(mapa).sort().reverse();
      for (const f of fechasDisponibles) {
        if (f <= ultimoDiaStr) {
          valorEncontrado = mapa[f];
          break;
        }
      }
    }

    valores[mes] = valorEncontrado || fallback;
  }

  return valores;
};

/**
 * Valor del dólar el día 10 (o más cercano anterior) de un mes dado.
 * Usado por el ER para cálculos de costos fijos.
 */
export const getDolarDia10 = (monthIndex, pais, year) => {
  const mapa = pais === "Chile" ? cache.dolarChile : cache.dolarPeru;
  const fallback = getDolarFallback(pais);

  if (!mapa || Object.keys(mapa).length === 0) return fallback;

  const fecha10 = `${year}-${String(monthIndex + 1).padStart(2, "0")}-10`;
  const valor = mapa[fecha10];
  if (valor !== undefined) return valor;

  const fechasDisponibles = Object.keys(mapa).sort().reverse();
  for (const f of fechasDisponibles) {
    if (f <= fecha10) return mapa[f];
  }
  return fallback;
};

// ─── Cross-rate ──────────────────────────────────────────────────────────────

/**
 * Cross-rate PEN/CLP para conversión entre países.
 * Usado por useCategoriaMoneda para convertir montos CLP a PEN.
 */
export const getCrossRate = () => {
  const rateCLP = getDolarFallback("Chile");
  const ratePEN = getDolarFallback("Perú");
  if (!rateCLP || !ratePEN) return 0;
  return ratePEN / rateCLP;
};

// ─── Conversión universal ────────────────────────────────────────────────────

/**
 * Convierte cualquier moneda a moneda local del país.
 * Reemplaza todas las funciones convertirAMonedaLocal dispersas.
 *
 * @param {number} valor - Monto a convertir
 * @param {string} monedaOriginal - "UF", "USD", "US$", "CLP", "PEN", etc.
 * @param {string} pais - "Chile" o "Perú"
 * @param {string} fechaStr - Fecha para cotización histórica (default: hoy)
 * @returns {number} Valor en moneda local
 */
export const convertirAMonedaLocal = (valor, monedaOriginal, pais, fechaStr = null) => {
  if (!valor) return 0;

  const num = parseFloat(valor) || 0;
  const monedaUpper = (monedaOriginal || "").toUpperCase();

  // UF solo Chile
  if (pais === "Chile" && monedaUpper.includes("UF")) {
    const fechaUsada = fechaStr || new Date().toISOString().split("T")[0];
    return Math.round(num * getUFValue(fechaUsada));
  }

  // Dólar → moneda local
  if (monedaUpper.includes("USD") || monedaUpper.includes("US$") || monedaUpper === "DÓLAR") {
    const fechaUsada = fechaStr || new Date().toISOString().split("T")[0];
    return Math.round(num * getDolarValue(fechaUsada, pais));
  }

  // Ya está en moneda local
  return num;
};
